import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck, ArrowUp, ArrowDown, Warning } from "@phosphor-icons/react";
import { CustomerLoadReport } from "@/components/CustomerLoadReport";

const media = (p) => `${API}/events/media/${p}?auth=${encodeURIComponent(localStorage.getItem("sv_token") || "")}`;
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" }) : "—");
const DIR = {
  loading: ["LOADED", "#30D158"], unloading: ["UNLOADED", "#0A84FF"], no_change: ["NO CHANGE", "#A1A1AA"],
  review: ["NEEDS REVIEW", "#FF9F0A"], at_dock: ["AT LOADING SPOT", "#BF5AF2"], container: ["CONTAINER · NOT COUNTED", "#636366"],
};

function Stat({ label, value, color, tid }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={tid}>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-2xl font-display font-black" style={{ color }}>{value}</div>
    </div>
  );
}

function Shot({ title, imgs, count, info, onZoom, tid }) {
  return (
    <div className="space-y-1" data-testid={tid}>
      <button onClick={() => imgs?.[0] && onZoom(imgs)} className="w-full aspect-video bg-black overflow-hidden border border-white/10">
        {imgs?.[0] ? <img src={media(imgs[0])} alt={title} className="w-full h-full object-contain" /> : <span className="text-[10px] text-[#71717A]">waiting…</span>}
      </button>
      <div className="text-[10px] font-mono-data text-[#A1A1AA]">{title}</div>
      <div className="font-display font-black text-lg">{count ?? "?"} <span className="text-[10px] font-mono-data text-[#71717A]">slabs{info?.confidence != null ? ` · ${info.confidence}%` : ""}{info?.cargo ? ` · ${info.cargo}` : ""}{info?.reads?.length > 1 ? ` · AI reads ${info.reads.join("/")}` : ""}</span></div>
    </div>
  );
}

function FixForm({ r, onDone }) {
  const [a, setA] = useState(r.arrive_slabs ?? "");
  const [d, setD] = useState(r.depart_slabs ?? "");
  const [p, setP] = useState(r.plate || "");
  const save = async () => {
    const body = { plate: p };
    if (a !== "") body.arrive_slabs = Number(a);
    if (d !== "") body.depart_slabs = Number(d);
    try { await api.patch(`/avstone/dock/${r.id}`, body); toast.success("Truck visit corrected"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const inp = "bg-black border border-white/20 px-2 py-1 font-mono-data text-sm w-24";
  return (
    <div className="flex flex-wrap gap-2 items-end border-t border-white/10 pt-3" data-testid={`dock-fix-${r.id}`}>
      <label className="text-[10px] font-mono-data text-[#71717A]">Slabs on arrival<br /><input type="number" min="0" value={a} onChange={(e) => setA(e.target.value)} className={inp} data-testid={`dock-fix-arrive-${r.id}`} /></label>
      <label className="text-[10px] font-mono-data text-[#71717A]">Slabs on departure<br /><input type="number" min="0" value={d} onChange={(e) => setD(e.target.value)} className={inp} data-testid={`dock-fix-depart-${r.id}`} /></label>
      <label className="text-[10px] font-mono-data text-[#71717A]">Plate<br /><input value={p} onChange={(e) => setP(e.target.value.toUpperCase())} className={inp + " w-32"} data-testid={`dock-fix-plate-${r.id}`} /></label>
      <button onClick={save} className="px-3 py-1.5 bg-[#30D158] text-black text-xs font-bold" data-testid={`dock-fix-save-${r.id}`}>Save</button>
    </div>
  );
}

function VisitRow({ r, onZoom, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [lab, col] = DIR[r.direction] || DIR.review;
  const n = r.direction === "loading" ? `+${r.loaded}` : r.direction === "unloading" ? `−${r.unloaded}` : "";
  const del = async () => { if (window.confirm("Remove this truck visit (false trigger)?")) { await api.delete(`/avstone/dock/${r.id}`); onChanged(); } };
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid={`dock-row-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-mono-data px-2 py-1 border" style={{ color: col, borderColor: col }} data-testid={`dock-direction-${r.id}`}>
            {r.direction === "loading" ? <ArrowUp className="inline mr-1" /> : r.direction === "unloading" ? <ArrowDown className="inline mr-1" /> : null}{lab} {n}
          </span>
          {r.needs_review && r.direction !== "review" && <span className="text-[10px] font-mono-data text-[#FF9F0A] border border-[#FF9F0A] px-1.5 py-0.5"><Warning className="inline" /> CHECK COUNT</span>}
          {r.reviewed && <span className="text-[10px] font-mono-data text-[#30D158]">STAFF CONFIRMED</span>}
          <span className="font-mono-data text-lg font-black tracking-widest" data-testid={`dock-plate-${r.id}`}>{r.plate || "plate —"}</span>
          {r.fleet && <span className="text-[10px] font-mono-data text-[#0A84FF] border border-[#0A84FF] px-1.5 py-0.5">OWN TRUCK {r.fleet_label}</span>}
          {r.customer && <span className="text-[10px] font-mono-data text-[#BF5AF2] border border-[#BF5AF2] px-1.5 py-0.5" data-testid={`dock-customer-${r.id}`}>{r.customer}</span>}
          {r.xcheck_conflict && <span className="text-[10px] font-mono-data text-[#FF3B30] border border-[#FF3B30] px-1.5 py-0.5" data-testid={`dock-xcheck-conflict-${r.id}`}>DOOR CAMERA DISAGREES</span>}
        </div>
        <div className="text-xs font-mono-data text-[#A1A1AA]">{r.dock} · {tm(r.arrive_ts)} → {r.depart_ts ? tm(r.depart_ts) : "still here"}</div>
      </div>
      {r.vehicle_desc && <div className="text-xs text-[#71717A]">{r.vehicle_desc}</div>}
      {r.xcheck && (
        <div className="text-xs font-mono-data text-[#A1A1AA] flex flex-wrap items-center gap-2" data-testid={`dock-xcheck-${r.id}`}>
          <span>Bay C door check: <b className="text-white">{{ out: "slabs went OUT (loading)", in: "slabs came IN (unloading)", both: "slabs both ways", none: "no slab movement seen" }[r.xcheck.direction] || "—"}</b> · {r.xcheck.confidence}%</span>
          {(r.xcheck_images || []).slice(0, 8).map((p) => (
            <button key={p} onClick={() => onZoom(r.xcheck_images)} className="w-14 h-8 bg-black overflow-hidden"><img src={media(p)} alt="door" className="w-full h-full object-cover" /></button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 max-w-2xl">
        <Shot title={`ARRIVED ${tm(r.arrive_ts)}`} imgs={r.arrive_images} count={r.arrive_slabs} info={r.arrive} onZoom={onZoom} tid={`dock-arrive-${r.id}`} />
        <Shot title={`LEFT ${tm(r.depart_ts)}`} imgs={r.depart_images} count={r.depart_slabs} info={r.depart} onZoom={onZoom} tid={`dock-depart-${r.id}`} />
      </div>
      <div className="flex gap-2">
        <button onClick={() => setEdit(!edit)} className="px-3 py-1 border border-white/20 text-xs hover:bg-white/10 transition-colors" data-testid={`dock-edit-btn-${r.id}`}>{edit ? "Cancel" : "Correct"}</button>
        <button onClick={del} className="px-3 py-1 border border-[#FF3B30]/40 text-[#FF3B30] text-xs hover:bg-[#FF3B30]/10 transition-colors" data-testid={`dock-delete-btn-${r.id}`}>Remove</button>
      </div>
      {edit && <FixForm r={r} onDone={() => { setEdit(false); onChanged(); }} />}
    </div>
  );
}

export default function AvStoneLoading() {
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/avstone/dock?date=${date}`).then((r) => { setD(r.data); setErr(""); })
    .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load")), [date]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const t = d?.totals || {};
  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="avstone-loading-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-2xl tracking-tight flex items-center gap-2"><Truck size={22} weight="bold" className="text-[#30D158]" /> Truck Loading · Slabs</h1>
          <p className="text-xs font-mono-data text-[#71717A] mt-1">Slabs are counted when a truck parks and again just before it leaves. More slabs when it leaves = LOADED, fewer = UNLOADED.</p>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-black border border-white/20 px-2 py-1 text-sm" data-testid="dock-date" />
      </div>
      {err && <div className="text-sm text-[#FF3B30] font-mono-data" data-testid="dock-error">{err}</div>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Slabs loaded" value={t.loaded ?? 0} color="#30D158" tid="dock-total-loaded" />
        <Stat label="Slabs unloaded" value={t.unloaded ?? 0} color="#0A84FF" tid="dock-total-unloaded" />
        <Stat label="Truck visits" value={t.visits ?? 0} tid="dock-total-visits" />
        <Stat label="Needs review" value={t.review ?? 0} color="#FF9F0A" tid="dock-total-review" />
        <Stat label="At loading spot now" value={t.at_dock ?? 0} color="#BF5AF2" tid="dock-total-atdock" />
      </div>
      <div className="text-xs font-mono-data text-[#A1A1AA]" data-testid="dock-summary">
        {t.trucks_loading ?? 0} truck(s) loaded · {t.trucks_unloading ?? 0} truck(s) unloaded · {t.containers ?? 0} container(s) not counted · totals exclude visits that need review until staff correct them
      </div>
      <CustomerLoadReport />
      <div className="space-y-3">
        {(d?.rows || []).map((r) => <VisitRow key={r.id} r={r} onZoom={setZoom} onChanged={load} />)}
        {d && !d.rows.length && <p className="text-sm text-[#71717A] font-mono-data" data-testid="dock-empty">No truck visits on this day yet.</p>}
      </div>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 gap-2 flex-wrap" onClick={() => setZoom(null)} data-testid="dock-zoom">
          {zoom.map((p) => <img key={p} src={media(p)} alt="" className="max-h-[85vh] max-w-[48vw] object-contain" />)}
        </div>
      )}
    </div>
  );
}
